import { createContext, useContext, useState, useCallback, useEffect } from 'react';

// Active Lookup shared across pages (ACT! 4.0 behavior: a lookup is a
// persistent filter — Contact List honors it until explicitly cleared).
// Shape: { label, count, query: {conditions, logic}|null, ids: [...]|null }
// Persisted in localStorage + synced across tabs via the storage event, so a
// lookup Crunchy applies in one tab shows up in every other open tab too.
const LookupCtx = createContext(null);
const STORAGE_KEY = 'sc_active_lookup';

export function LookupProvider({ children }) {
  const [activeLookup, setActiveLookupState] = useState(() => {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY)) || null; } catch { return null; }
  });

  const setActiveLookup = useCallback((lk) => {
    setActiveLookupState(lk);
    try {
      if (lk) localStorage.setItem(STORAGE_KEY, JSON.stringify(lk));
      else localStorage.removeItem(STORAGE_KEY);
    } catch {}
  }, []);

  // Cross-tab sync: when another tab changes the active lookup, mirror it here.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== STORAGE_KEY) return;
      try { setActiveLookupState(e.newValue ? JSON.parse(e.newValue) : null); } catch {}
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const clearActiveLookup = useCallback(() => setActiveLookup(null), [setActiveLookup]);

  return (
    <LookupCtx.Provider value={{ activeLookup, setActiveLookup, clearActiveLookup }}>
      {children}
    </LookupCtx.Provider>
  );
}

export function useLookup() {
  return useContext(LookupCtx) || { activeLookup: null, setActiveLookup: () => {}, clearActiveLookup: () => {} };
}
